import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { type LatLng } from '~/_shared/types/latLng';
import { createUuid } from '~/_shared/utils/createUuid';
import { clamp } from '~/_shared/utils/number/number.helpers';
import {
  DRAWING_TOOL_CIRCLE_MAX_RADIUS, DRAWING_TOOL_CIRCLE_MIN_RADIUS,
} from '~/drawingTool/drawingTool.constants';
import { DrawingTool } from '~/drawingTool/drawingTool.enums';
import { useDrawingToolSizePerPixelRatio } from '~/drawingTool/hooks/useDrawingToolSizePerPixelRatio';
import { useActiveDrawingInstanceZIndex } from '~/map/zIndexes/useDrawingInstanceZIndex.hook';
import {
  DrawingToolMode, useDrawingToolModeSelector,
} from '~/store/frontendState/mapTools/drawingTool/drawingTool.selectors';
import { drawingItemsAddItem } from '~/store/mapSettings/drawing/items/drawingItems.actionCreators';
import { DrawingItemPlacement } from '~/store/mapSettings/drawing/items/drawingItems.types';
import { type DrawingCircleSettingsState } from '~/store/mapSettings/drawing/settings/circle/drawingCircleSettings.state';
import { useDrawCircle } from '../../mapObjects/mapCircle/useDrawCircle.hook';
import { type DrawingToolManager } from '../drawingToolManager';
import { DrawingToolCircleInstanceCircleContainer } from './drawingToolCircleInstanceCircle.container';

type DrawingToolCircleCreateNewInstanceProps = Readonly<{
  manager: DrawingToolManager;
  settings: DrawingCircleSettingsState;
}>;

export const DrawingToolCircleCreateNewInstanceContainer: FC<DrawingToolCircleCreateNewInstanceProps> = ({
  settings,
}) => {
  const drawingToolMode = useDrawingToolModeSelector();
  const dispatch = useDispatch();
  const zIndex = useActiveDrawingInstanceZIndex();
  const sizePerPixelRatio = useDrawingToolSizePerPixelRatio();

  const onCircleDrawn = useCallback(({ center, radiusInPixels }: { center: LatLng; radiusInPixels: number }) => {
    const id = createUuid();

    const ratio = settings.scalesWithMapZoom ? sizePerPixelRatio : 1;
    const radius = clamp(radiusInPixels, { min: DRAWING_TOOL_CIRCLE_MIN_RADIUS, max: DRAWING_TOOL_CIRCLE_MAX_RADIUS }) * ratio;

    dispatch(drawingItemsAddItem(
      id,
      {
        type: DrawingTool.Circle,
        value: {
          id,
          center,
          placement: DrawingItemPlacement.Default,
          settings: {
            ...settings,
            radius,
          },
        },
      }));
  }, [dispatch, settings, sizePerPixelRatio]);

  const {
    center: circleCenter,
    radiusInPixels: temporaryCircleRadiusInPx,
  } = useDrawCircle({
    isActive: drawingToolMode === DrawingToolMode.DrawingItems,
    minPixels: DRAWING_TOOL_CIRCLE_MIN_RADIUS,
    maxPixels: DRAWING_TOOL_CIRCLE_MAX_RADIUS,
    onCircleDrawn,
  });

  if (!circleCenter || !temporaryCircleRadiusInPx) {
    return null;
  }

  return (
    <DrawingToolCircleInstanceCircleContainer
      instance={{
        id: 'drawing-tool-drawing-new-circle',
        center: circleCenter,
        placement: DrawingItemPlacement.Default,
        settings: {
          ...settings,
          scalesWithMapZoom: false,
          radius: temporaryCircleRadiusInPx,
        },
      }}
      zIndex={zIndex}
    />
  );
};
